import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import toast from 'react-hot-toast';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FaCheckDouble } from "react-icons/fa6";
import { useLocation } from "react-router-dom";
import PageHeader from '../../components/Shared/PageHeader';
import SharedButton from "../../components/Shared/SharedButton";
import TextInput from "../../components/Shared/TextInput";
import SharedDropdown from "../../components/Shared/SharedDropdown";
import {
  createNewCostCenter,
  editCostCenter,
  getCostCenterList,
} from "../../store/slices/CostCenterSlice";
import { fetchData } from '../../helpers/fetchData';

export default function CreateCostCenter({
  setCreateDialogVisible,
}) {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { isLoading } = useSelector((state) => state.costCenter);
  const [loading, setLoading] = useState(false);
  const [costCenters, setCostCenters] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const editData = location?.state?.editData || null;


  const fetchCostCenter = async () => {
    setLoading(true)
    const data = await fetchData("POST", "getCostcenter");
    setLoading(false)
    if (data) setCostCenters(data?.data);
  }

  useEffect(() => {
    fetchCostCenter()
  }, [dispatch]);

  const parentCostCenterOptions = Array.isArray(costCenters)
    ? costCenters
      ?.map((cc) => ({
        label: `${cc.code} - ${cc.name_en}`,
        value: cc.code,
      }))
    : [];

  const categoryOptions = [
    { label: t("Parent"), value: "parent" },
    { label: t("Child"), value: "child" },
  ];

  const initialValues = {
    name_en: editData?.name_en || "",
    name_ar: editData?.name_ar || "",
    code: editData?.code || "",
    category: editData?.category || "",
    parent_code: editData?.parent_code ?? "",
    description: editData?.description || "",
  };

  const validationSchema = Yup.object().shape({
    name_en: Yup.string().required(t("required")),
    name_ar: Yup.string().required(t("required")),
    code: Yup.number()
      .required(t("required"))
      .integer(t("must_be_integer"))
      .typeError(t("must_be_number")),
    category: Yup.string()
      .oneOf(["parent", "child"], t("invalid_category"))
      .required(t("required")),
    parent_code: Yup.number()
      .typeError(t("must_be_number"))
      .when("category", {
        is: "parent",
        then: (schema) =>
          schema
            .required(t("required"))
            .oneOf([0], t("parent_code_must_be_0")),
        otherwise: (schema) =>
          schema
            .required(t("required"))
            .min(1, t("parent_code_must_be_greater_than_0")),
      }),
    description: Yup.string().optional(),
  });

  const onSubmit = async (values, { resetForm }) => {
    try {
      const payload = {
        name_en: values.name_en,
        name_ar: values.name_ar,
        code: parseInt(values.code, 10),
        category: values.category,
        parent_code: values.category === "parent" ? 0 : parseInt(values.parent_code, 10),
        ...(values.description ? { description: values.description } : {}),
      };

      if (editData) {
        const resultAction = await dispatch(
          editCostCenter({ data: payload, id: editData?.id })
        );
        if (editCostCenter.fulfilled.match(resultAction)) {
          toast.success(resultAction?.payload?.message || t('costCenter_updated'));
          if (setCreateDialogVisible && typeof setCreateDialogVisible === 'function') {
            setCreateDialogVisible(false);
            dispatch(getCostCenterList({ page: 1, per_page: 10 }));
          } else {
            navigate('/manager/costCenter');
          }
          resetForm();
        }
      } else {
        const resultAction = await dispatch(createNewCostCenter(payload));
        if (createNewCostCenter.fulfilled.match(resultAction)) {
          toast.success(resultAction?.payload?.message || t('costCenter_created'));
          if (setCreateDialogVisible && typeof setCreateDialogVisible === 'function') {
            setCreateDialogVisible(false);
            dispatch(getCostCenterList({ page: 1, per_page: 10 }));
          } else {
            navigate('/manager/costCenter');
          }
          resetForm();
        }
      }
    } catch (error) {
      console.error("Submission error:", error);
    }
  };

  return (
    <div className="flex flex-col w-full gap-2 max-h-[calc(100vh-150px)]">
      <PageHeader title={t(editData ? "Update Cost Center" : "Create Cost Center")} backPath={'/manager/costCenter'} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">

              <TextInput
                label={t("name_en")}
                name="name_en"
                placeholder={`${t("enter")} ${t("name_en")}`}
              />

              <TextInput
                label={t("name_ar")}
                name="name_ar"
                placeholder={`${t("enter")} ${t("name_ar")}`}
              />

              <TextInput
                label={t("code")}
                name="code"
                placeholder={`${t("enter")} ${t("code")}`}
              />

              <SharedDropdown
                label={t("category")}
                name="category"
                options={categoryOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => {
                  formik.setFieldValue("category", e.value);
                  if (e.value === "parent") {
                    formik.setFieldValue("parent_code", 0);
                  } else {
                    formik.setFieldValue("parent_code", "");
                  }
                }}
                value={formik.values.category}
              />
              {formik.values.category === "child" && (
                <SharedDropdown
                  label={t("parent_code")}
                  name="parent_code"
                  options={parentCostCenterOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => {
                    formik.setFieldValue("parent_code", e.value);
                  }}
                  value={formik.values.parent_code}
                  filter
                />
              )}
              <TextInput
                label={t("description")}
                name="description"
                placeholder={`${t("enter")} ${t("description")}`}
              />
            </div>

            <div className="flex justify-end gap-2">
              <SharedButton
                type="button"
                icon="pi pi-times"
                label={t("Cancel")}
                buttonType="reset"
                onClick={() => {
                  navigate("/manager/costCenter");
                  formik.resetForm();
                }}
              />
              <SharedButton
                type="submit"
                icon={<FaCheckDouble size={12} className='me-2' />}
                loading={isLoading}
                disabled={loading}
                label={editData ? t("Update") : t("Submit")}
                className="submit-btn"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}